import { createContext, useContext } from "react";
import type { Dispatch } from "react";
import type { AppState } from "../lib/db";
import { applyAnswer } from "../lib/srs";
import type { Deck, ExamId, SessionLog, Settings, WordProgress } from "../lib/types";
import type { SyncData } from "../lib/sync";

export type AppAction =
  | { type: "hydrate"; state: AppState }
  | { type: "answer"; wordId: string; correct: boolean; deckId: string }
  | { type: "addDeck"; deck: Deck }
  | { type: "removeDeck"; deckId: string }
  | { type: "updateDeck"; deckId: string; patch: Partial<Omit<Deck, "id" | "createdAt">> }
  | { type: "addWordsToDeck"; deckId: string; wordIds: string[] }
  | { type: "removeWordFromDeck"; deckId: string; wordId: string }
  | { type: "updateSettings"; partial: Partial<Settings> }
  | { type: "logSession"; log: SessionLog }
  | { type: "star"; wordId: string }
  | { type: "advance"; wordId: string; nextDue: string }
  | { type: "setTargetExam"; exam: ExamId }
  | { type: "setTargetDeadline"; deadline: string | undefined }
  | { type: "setOnboarded"; value: boolean }
  | { type: "mergeRemote"; data: SyncData };

export function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "hydrate":
      return action.state;
    case "answer": {
      const now = Date.now();
      const existing = state.progress[action.wordId];
      const base: WordProgress = existing ?? {
        wordId: action.wordId,
        deckId: action.deckId,
        stage: 0,
        seen: 0,
        lapses: 0,
        leech: false,
        mastered: false,
        starred: false,
        nextDue: new Date(now).toISOString(),
        updatedAt: new Date(now).toISOString(),
      };
      return {
        ...state,
        progress: { ...state.progress, [action.wordId]: applyAnswer(base, action.correct, now) },
      };
    }
    case "addDeck":
      return { ...state, decks: [...state.decks, action.deck] };
    case "removeDeck":
      return { ...state, decks: state.decks.filter((d) => d.id !== action.deckId) };
    case "updateDeck":
      return {
        ...state,
        decks: state.decks.map((d) =>
          d.id === action.deckId ? { ...d, ...action.patch, updatedAt: new Date().toISOString() } : d),
      };
    case "addWordsToDeck":
      return {
        ...state,
        decks: state.decks.map((d) => {
          if (d.id !== action.deckId) return d;
          const merged = [...new Set([...d.wordIds, ...action.wordIds])];
          return { ...d, wordIds: merged, updatedAt: new Date().toISOString() };
        }),
      };
    case "removeWordFromDeck":
      return {
        ...state,
        decks: state.decks.map((d) =>
          d.id === action.deckId
            ? { ...d, wordIds: d.wordIds.filter((id) => id !== action.wordId), updatedAt: new Date().toISOString() }
            : d),
      };
    case "updateSettings":
      return { ...state, settings: { ...state.settings, ...action.partial } };
    case "logSession":
      return { ...state, logs: [...state.logs, action.log] };
    case "star": {
      const progress = state.progress[action.wordId];
      if (!progress) return state;
      return {
        ...state,
        progress: {
          ...state.progress,
          [action.wordId]: { ...progress, starred: !progress.starred },
        },
      };
    }
    case "advance": {
      const progress = state.progress[action.wordId];
      if (!progress) {
        return {
          ...state,
          progress: {
            ...state.progress,
            [action.wordId]: {
              wordId: action.wordId,
              deckId: state.decks[0]?.id || "",
              stage: 0,
              seen: 0,
              lapses: 0,
              leech: false,
              mastered: false,
              starred: false,
              nextDue: action.nextDue,
              updatedAt: action.nextDue,
            },
          },
        };
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          [action.wordId]: { ...progress, nextDue: action.nextDue, updatedAt: action.nextDue },
        },
      };
    }
    case "setTargetExam":
      return { ...state, targetExam: action.exam };
    case "setTargetDeadline":
      return { ...state, targetDeadline: action.deadline };
    case "setOnboarded":
      return { ...state, onboarded: action.value };
    case "mergeRemote":
      // 只合併業務欄位；onboarding 設定（targetExam/targetDeadline/onboarded）保留本機值
      return {
        ...state,
        decks: action.data.decks ?? state.decks,
        progress: action.data.progress ?? state.progress,
        logs: action.data.logs ?? state.logs,
        settings: action.data.settings ? { ...state.settings, ...action.data.settings } : state.settings,
      };
  }
}

export interface AppStore {
  state: AppState;
  dispatch: Dispatch<AppAction>;
  ready: boolean;
}

export const AppStoreContext = createContext<AppStore | null>(null);

export function useAppStore(): AppStore {
  const context = useContext(AppStoreContext);
  if (!context) throw new Error("useAppStore must be used within <AppStoreProvider>");
  return context;
}
